// Live presence on the ticket-detail page via Supabase Realtime.
//
// Each open ticket-detail tab joins a presence channel keyed by ticket id.
// The list of OTHER teammates currently on that channel surfaces as avatars
// in the ticket header — so when Rex opens a ticket Liz is already reading,
// Rex sees Liz's avatar there and knows not to double up.
//
// Why Realtime presence specifically (instead of polling): presence is the
// one feature where polling cadence shows. A 10s poll feels broken — by the
// time the badge appears the other person has moved on. Realtime updates
// arrive within ~100ms of the join/leave event. The channel only lives
// during the ticket-detail mount, so we don't carry a long-lived WebSocket
// across the app.
//
// Multi-tab dedupe: the presence key is the user's slack_user_id, so two
// tabs from the same person register as one entry (Realtime tracks the
// latest payload per key). The display also dedupes defensively.

import { useEffect, useRef, useState } from 'react';
import { supabase } from './supabase';

export interface PresencePayload {
  slack_user_id: string;
  name: string;
  avatar_url: string | null;
  email: string;
  /** ISO8601 — when this tab joined the channel. Used to display "viewing for Xm". */
  joined_at: string;
  /** Has text in the reply box right now ("Liz is writing a reply"). */
  drafting?: boolean;
}

export interface TicketViewer extends PresencePayload {}

export function useTicketPresence(
  ticketId: string | undefined,
  me:
    | {
        slack_user_id: string;
        name: string;
        avatar_url: string | null;
        email: string;
      }
    | null,
  drafting = false,
): TicketViewer[] {
  const [viewers, setViewers] = useState<TicketViewer[]>([]);
  // Kept in refs so a change in "drafting" re-announces on the SAME channel instead of
  // tearing it down (which would flicker everyone's viewer list).
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const payloadRef = useRef<PresencePayload | null>(null);
  const draftingRef = useRef(drafting);
  draftingRef.current = drafting;

  useEffect(() => {
    if (!ticketId) return;
    if (!me?.slack_user_id) return;

    const channelName = `presence:ticket:${ticketId}`;
    const channel = supabase.channel(channelName, {
      config: {
        // Keying by slack_user_id means a single user with two tabs counts
        // as one presence entry (the latest tab's payload wins). Without
        // this, every tab is a separate row.
        presence: { key: me.slack_user_id },
      },
    });

    const myPayload: PresencePayload = {
      slack_user_id: me.slack_user_id,
      name: me.name,
      avatar_url: me.avatar_url,
      email: me.email,
      joined_at: new Date().toISOString(),
      drafting: draftingRef.current,
    };
    channelRef.current = channel;
    payloadRef.current = myPayload;

    const computeViewers = () => {
      const state = channel.presenceState<PresencePayload>();
      // state = { [presence_key]: PresencePayload[] } — the array can hold
      // multiple payloads for the same key (e.g. two tabs); we take the
      // first since we treat them as equivalent.
      const seen = new Set<string>();
      const out: TicketViewer[] = [];
      for (const key of Object.keys(state)) {
        // Skip self.
        if (key === me.slack_user_id) continue;
        const list = state[key] as PresencePayload[];
        const first = list?.[0];
        if (!first || !first.slack_user_id) continue;
        if (seen.has(first.slack_user_id)) continue;
        seen.add(first.slack_user_id);
        out.push(first);
      }
      // Stable order — by joined_at ascending so the longest-viewing person
      // is first. Avoids the avatar row jumping around on every sync tick.
      out.sort((a, b) => a.joined_at.localeCompare(b.joined_at));
      setViewers(out);
    };

    channel
      .on('presence', { event: 'sync' }, computeViewers)
      .on('presence', { event: 'join' }, computeViewers)
      .on('presence', { event: 'leave' }, computeViewers)
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({ ...myPayload, drafting: draftingRef.current });
        }
      });

    return () => {
      // untrack first so other clients see the leave event before the
      // socket goes away. Then remove the channel.
      channelRef.current = null;
      void channel.untrack().finally(() => {
        void supabase.removeChannel(channel);
      });
      setViewers([]);
    };
  }, [ticketId, me?.slack_user_id, me?.name, me?.avatar_url, me?.email]);

  // Started or stopped typing a reply: tell the others on this ticket.
  useEffect(() => {
    const channel = channelRef.current;
    const payload = payloadRef.current;
    if (!channel || !payload) return;
    void channel.track({ ...payload, drafting }).catch(() => {});
  }, [drafting]);

  return viewers;
}
